<template>
  <div v-if="displayTiles?.components.length">
    <component
      :is="AwStandardTypeConfig[carouselType].component"
      v-if="AwStandardTypeConfig[carouselType]?.component"
      :title-attrs="titleAttrs"
      :max-title-length="maxTitleLength"
      :text-line-clamp="textLineClamp"
      :title-line-clamp="titleLineClamp"
      :content="displayTiles"
      :analytics-measurable="false"
      :analytics-banner-interaction="EMPTY_ANALYTICS_BANNER_INTERACTION"
    />
    <aw-carousel-v3219
      v-else
      :content="displayTiles"
      :title-attrs="titleAttrs"
      :analytics-measurable="false"
      :analytics-banner-interaction="EMPTY_ANALYTICS_BANNER_INTERACTION"
    />
    <aw-separator v-if="showSeparator" />
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent } from 'vue';
  import { useNuxtApp, useAsyncData } from 'nuxt/app';
  import { AW_AX_CACHE_GLOBAL } from '~~/common/plugins/axios/awAxCache';
  import otqs from '~~/common/utils/objectToQueryString';
  import { setErrorNotification } from '~~/common/utils/helper';
  import { EMPTY_ANALYTICS_BANNER_INTERACTION } from '~~/common/utils/AnalyticsBannerInteraction';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import AwStandardTypeConfig, { SCT_FULL_CAROUSEL_V3_21_9 } from '~~/common/config/AwStandardTypeConfig';

  const AwCarouselV3219 = defineAsyncComponent(() => import('~~/common/components/AwStandardPartials/AwCarouselV3/AwCarouselV3_21_9'));

  const nuxtApp = useNuxtApp();
  const {
    $awt,
    $api,
    $logger,
  } = nuxtApp;

  const props = defineProps({
    pageType: {
      type: String,
      required: true,
    },
    showSeparator: {
      type: Boolean,
      default: true,
    },
    carouselType: {
      type: String,
      default: SCT_FULL_CAROUSEL_V3_21_9,
    },
    titleLineClamp: {
      type: Number,
      default: 0,
    },
    maxTitleLength: {
      type: Number,
      default: 0,
    },
    textLineClamp: {
      type: Number,
      default: 0,
    },
    buttonItems: {
      type: Array,
      default: () => {
        return [
          {
            token: 'aw.pages.aggregated_common.tile.highlighted_button_title',
            type: 'primary-dark',
            mobileType: 'primary-dark',
          },
        ];
      },
    },
    titleAttrs: {
      type: Object,
      default: () => {
        return {
          tag: 'h1',
          level: '1',
        };
      },
    },
  });
  const dataKey = computed(() => {
    return `cms/tag-groups/${props.pageType}${otqs({
      page: 1,
      limit: 6,
      highlighted: true,
    })}`;
  });
  const { data } = await useAsyncData(
    dataKey.value,
    async () => {
      return await fetchData();
    },
    {
      default: () => [],
    },
  );
  const displayTiles = computed(() => {
    const displayData = data.value.map((tile, index) => ({
      id: index,
      type: 'standard',
      layoutTemplate: 'carousel_v3_content',
      title: tile.title,
      contentText: tile?.lead,
      dateTag: tile.publishedAt,
      showPlaceholder: true,
      media: {
        contentImage: {
          url: tile.media?.carouselCardImage?.url,
          alt: tile.media?.carouselCardImage?.alt,
        },
      },
      button: {
        alignment: 'flex-start',
        items: props.buttonItems.map(buttonItem => ({
          ...buttonItem,
          text: $awt(buttonItem.token),
          link: tile?.url,
        })),
      },
      contentBackgroundColorOnly: false,
      horizontalLayout: false,
      tags: ['inspiration'].includes(props.pageType) ? tile.tags : [],
    }));
    return {
      id: 0,
      layoutType: 'full_tiles',
      spacingType: 'positive',
      fullWidth: false,
      measurablePromotion: false,
      components: displayData,
    };
  });

  async function fetchData () {
    try {
      const result = await $api.$get(dataKey.value, { awAxCache: AW_AX_CACHE_GLOBAL });
      return (Array.isArray(result?.contentVersions)
        ? result.contentVersions
        : []
      );
    } catch (error) {
      $logger.error(error);
      setErrorNotification({
        nuxtApp,
        error,
      });
      return [];
    }
  }
</script>
